import React from 'react';
import PropTypes from 'prop-types';
import { Formik, useFormikContext, getIn } from 'formik';
import * as Yup from 'yup';
import { Button } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { lookupsJson } from '@ourbranch/lookups';
import { v4 as uuidv4 } from 'uuid';

import Field from 'core/components/form/form.v2';
import { SubRiskCollapsibleSection } from 'common/components/farm/locations/risks/sub-risks/sub-risk-collapsible-section';
import FormSectionWrapper from 'common/components/form-section-wrapper';
import CollapsibleListItem from 'common/components/farm/locations/risks/sub-risks/collapsible-list-item';
import { AppliedTag } from 'common/components/farm/applied-tag';
import useStyles from '../liability-coverages-styles';
import AddFormContainer from 'common/components/farm/add-form-container';

const occupancyOptions = lookupsJson.farm.occupancyOptions;

const getOccupancyLabel = (type) => occupancyOptions.find((o) => o.id === type)?.value || type;

const validationSchema = Yup.object().shape({
  type: Yup.string().required('Occupancy type is required'),
  description: Yup.string().required('Occupancy description is required'),
  location: Yup.string().required('Location description is required')
});

const FIELD_NAME = 'farmLiabilityCoverage.occupancies';

const Occupancy = ({ disabled = false, onChangeActive }) => {
  const classes = useStyles();
  const { values, setFieldValue, setFieldTouched } = useFormikContext();

  const occupancies = getIn(values, FIELD_NAME) || [];

  return (
    <div className={classes.sectionContainer} id="farmLiabilityCoverage.occupancies">
      <SubRiskCollapsibleSection
        type="secondary"
        content={<div className={classes.headerText}>Office, professional, private school, or studio occupancy</div>}
        contentRight={occupancies.length > 0 ? <AppliedTag /> : null}
        initialOpen={occupancies.length > 0}
      >
        <FormSectionWrapper
          sectionTitle="Scheduled items"
          totalLabel="items"
          totalCount={occupancies.length}
          emptyStateTitle="There are no items added yet"
          emptyStateSubtitle="To add one select from below"
        >
          {occupancies.map((occupancy, index) => (
            <CollapsibleListItem
              key={`occupancy-${index}`}
              title={getOccupancyLabel(occupancy.type)}
              rightSideValues={[]}
              onDelete={() => {
                const newOccupancies = occupancies.filter((_, i) => i !== index);
                setFieldValue(FIELD_NAME, newOccupancies);
                setFieldTouched(FIELD_NAME);
                onChangeActive();
              }}
              disabled={disabled}
            >
              <Field
                xs={12}
                type="textarea"
                id={`${FIELD_NAME}[${index}].description`}
                name={`${FIELD_NAME}[${index}].description`}
                mode="dark"
                label="Occupancy description"
                disabled={disabled}
                onChange={onChangeActive}
              />
              <Field
                xs={12}
                type="textarea"
                id={`${FIELD_NAME}[${index}].location`}
                name={`${FIELD_NAME}[${index}].location`}
                mode="dark"
                label="Location"
                disabled={disabled}
                onChange={onChangeActive}
              />
            </CollapsibleListItem>
          ))}

          <AddFormContainer className={classes.addFormContainer}>
            <Formik
              initialValues={{
                type: '',
                description: '',
                location: ''
              }}
              validateOnChange={false}
              validateOnBlur={false}
              validationSchema={validationSchema}
              onSubmit={(val, { resetForm }) => {
                const newOccupancy = {
                  id: uuidv4(),
                  type: val.type,
                  description: val.description,
                  location: val.location
                };
                setFieldValue(FIELD_NAME, [...occupancies, newOccupancy]);
                setFieldTouched(FIELD_NAME);
                onChangeActive();
                resetForm();
              }}
            >
              {({ submitForm, values: formValues }) => (
                <Grid container spacing={2}>
                  <Field
                    xs={12}
                    type="select"
                    id="type"
                    name="type"
                    mode="dark"
                    label="Type of occupancy"
                    options={occupancyOptions}
                    disabled={disabled}
                  />
                  {formValues.type && (
                    <>
                      <Field
                        xs={12}
                        type="textarea"
                        id="description"
                        name="description"
                        mode="dark"
                        label="Occupancy description (required)"
                        disabled={disabled}
                      />
                      <Field
                        xs={12}
                        type="textarea"
                        id="location"
                        name="location"
                        mode="dark"
                        label="Location description (required)"
                        disabled={disabled}
                      />
                      <Grid container alignItems="center" className={classes.addButtonRow}>
                        <Button
                          type="button"
                          mode="big"
                          variant="contained"
                          color="secondary"
                          className={classes.addButton}
                          disabled={disabled}
                          onClick={submitForm}
                        >
                          Add Item
                        </Button>
                      </Grid>
                    </>
                  )}
                </Grid>
              )}
            </Formik>
          </AddFormContainer>
        </FormSectionWrapper>
      </SubRiskCollapsibleSection>
    </div>
  );
};

Occupancy.propTypes = {
  disabled: PropTypes.bool.isRequired,
  onChangeActive: PropTypes.func.isRequired
};

export default Occupancy;
